/**
 * PaginaGebruikMeter — onzichtbaar; meldt elke routewissel als één beacon (zie
 * utils/paginaGebruik.js). Alleen gemount binnen de ingelogde AppShell. Een directe
 * redirect (/ → /vandaag) wordt samengevoegd (debounce), en de tijd telt vanaf het
 * moment van de wissel, niet vanaf het versturen.
 *
 * Token: ALLEEN stil (Codex-review 26 sep). authFetch kan bij een verlopen sessie een
 * login-popup openen; een meting mag dat nooit veroorzaken. Dus: dev-sessietoken, of
 * acquireTokenSilent op het actieve account — lukt dat niet stil of is het token
 * verlopen, dan wordt deze meting overgeslagen.
 */
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { getMsalInstance } from '../utils/msalSingleton';
import { getDevSession } from '../utils/devSession';
import { loginRequest } from '../authConfig';
import { BACKEND_URL } from '../utils/apiConfig';
import { maakMeter, DEBOUNCE_MS } from '../utils/paginaGebruik';

function nogGeldig(token, margeSec = 60) {
  try {
    const { exp } = JSON.parse(atob(token.split('.')[1]));
    return Date.now() < exp * 1000 - margeSec * 1000;
  } catch {
    return false;
  }
}

/** Authorization-header zonder enige interactie; null als dat niet stil lukt. */
export async function stilleAuthHeader() {
  try {
    const dev = getDevSession();
    if (dev?.token) return { Authorization: `Bearer ${dev.token}` };
    const msal = getMsalInstance();
    if (!msal) return null;
    const alle = msal.getAllAccounts() || [];
    const account = msal.getActiveAccount() || (alle.length === 1 ? alle[0] : null);
    if (!account) return null;
    const res = await msal.acquireTokenSilent({ ...loginRequest, account });
    if (!res?.idToken || !nogGeldig(res.idToken)) return null;
    return { Authorization: `Bearer ${res.idToken}` };
  } catch {
    return null; // InteractionRequired e.d.: niet meten, nooit een popup
  }
}

export async function verstuurPaginaweergave(event) {
  const auth = await stilleAuthHeader();
  if (!auth) return false;
  try {
    // keepalive: het verzoek overleeft ook een wissel of het sluiten van het tabblad.
    // sendBeacon kan geen Authorization-header meesturen, vandaar fetch.
    await fetch(`${BACKEND_URL}/Api/gebruik/paginaweergave`, {
      method: 'POST',
      keepalive: true,
      headers: { 'Content-Type': 'application/json', ...auth },
      body: JSON.stringify(event),
    });
    return true;
  } catch {
    return false; // meten is nooit belangrijker dan werken
  }
}

export default function PaginaGebruikMeter({ verstuur = verstuurPaginaweergave }) {
  const { pathname } = useLocation();
  const meter = useRef(null);
  if (!meter.current) meter.current = maakMeter({ verstuur });

  useEffect(() => {
    const t = Date.now();
    const timer = setTimeout(() => meter.current.bezoek(pathname, t), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [pathname]);

  return null;
}
